import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useFormatMoneda } from '../utils/moneda';

const EMPTY_CREDITO = { cliente_id: '', monto: '', tasa_interes: 0, num_cuotas: '', frecuencia: 'mensual', fecha_inicio: '', observaciones: '' };
const EMPTY_REFI = { tasa_interes: 0, num_cuotas: '', frecuencia: 'mensual', fecha_inicio: new Date().toISOString().split('T')[0], observaciones: '' };
const ESTADOS_COLOR = { activo: '#34d399', cancelado: '#94a3b8', vencido: '#f87171', refinanciado: '#fbbf24' };

export default function Creditos() {
  const formatMoneda = useFormatMoneda();
  const [creditos, setCreditos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modal, setModal] = useState(false);
  const [cuotasModal, setCuotasModal] = useState(null);
  const [refiModal, setRefiModal] = useState(null); // crédito a refinanciar
  const [refiForm, setRefiForm] = useState(EMPTY_REFI);
  const [form, setForm] = useState(EMPTY_CREDITO);
  const [filtroEstado, setFiltroEstado] = useState('activo');
  const [loading, setLoading] = useState(false);

  function cargar() {
    api.get('/creditos', { params: { estado: filtroEstado } }).then(r => setCreditos(r.data)).catch(() => {});
    api.get('/clientes', { params: { activo: 1 } }).then(r => setClientes(r.data)).catch(() => {});
  }

  useEffect(() => { cargar(); }, [filtroEstado]);

  async function crear(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/creditos', { ...form, monto: parseFloat(form.monto), num_cuotas: parseInt(form.num_cuotas), tasa_interes: parseFloat(form.tasa_interes) });
      toast.success('Crédito creado');
      setModal(false);
      setForm(EMPTY_CREDITO);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear crédito');
    } finally {
      setLoading(false);
    }
  }

  async function verCuotas(credito_id) {
    try {
      const res = await api.get(`/creditos/${credito_id}`);
      setCuotasModal(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error cargando cuotas');
    }
  }

  async function refinanciar(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(`/creditos/${refiModal.id}/refinanciar`, {
        ...refiForm,
        num_cuotas: parseInt(refiForm.num_cuotas),
        tasa_interes: parseFloat(refiForm.tasa_interes)
      });
      toast.success(`Crédito refinanciado. Nuevo crédito #${res.data.nuevo_credito_id} por ${formatMoneda(res.data.saldo_refinanciado)}`);
      setRefiModal(null);
      setRefiForm(EMPTY_REFI);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al refinanciar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Créditos</h2>
        <button style={styles.btnPrimary} onClick={() => { setForm(EMPTY_CREDITO); setModal(true); }}>+ Nuevo Crédito</button>
      </div>
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        {['activo', 'cancelado', 'vencido', 'refinanciado'].map(e => (
          <button key={e} onClick={() => setFiltroEstado(e)}
            style={{ ...styles.pill, background: filtroEstado === e ? ESTADOS_COLOR[e] : '#1e293b', color: filtroEstado === e ? '#0f172a' : '#94a3b8' }}>
            {e}
          </button>
        ))}
      </div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th>Cliente</th><th>Monto</th><th>Cuotas</th><th>Frecuencia</th><th>Inicio</th><th>Avance</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {creditos.map(c => (
              <tr key={c.id} style={styles.row}>
                <td>{c.cliente_nombre}</td>
                <td>{formatMoneda(parseFloat(c.monto))}</td>
                <td>{c.cuotas_pagadas}/{c.total_cuotas}</td>
                <td>{c.frecuencia}</td>
                <td>{c.fecha_inicio}</td>
                <td>
                  <div style={styles.progressWrap}>
                    <div style={{ ...styles.progressBar, width: `${(c.cuotas_pagadas / c.total_cuotas) * 100}%` }} />
                  </div>
                </td>
                <td>
                  <button style={styles.btnEdit} onClick={() => verCuotas(c.id)}>Ver cuotas</button>
                  {c.estado === 'activo' && (
                    <button style={{ ...styles.btnEdit, color: '#fbbf24', marginLeft: 4 }} onClick={() => { setRefiModal(c); setRefiForm({ ...EMPTY_REFI, fecha_inicio: new Date().toISOString().split('T')[0] }); }}>Refinanciar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {creditos.length === 0 && <p style={styles.empty}>No hay créditos {filtroEstado}s</p>}
      </div>

      {modal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Nuevo Crédito</h3>
            <form onSubmit={crear}>
              <div style={styles.field}>
                <label style={styles.label}>Cliente</label>
                <select value={form.cliente_id} onChange={e => setForm({ ...form, cliente_id: e.target.value })} required style={styles.input}>
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.documento}</option>)}
                </select>
              </div>
              {[['monto', 'Monto del crédito', 'number'], ['tasa_interes', 'Tasa de interés (%)', 'number'], ['num_cuotas', 'Número de cuotas', 'number'], ['fecha_inicio', 'Fecha de inicio', 'date']].map(([key, label, type]) => (
                <div key={key} style={styles.field}>
                  <label style={styles.label}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} required step="any" style={styles.input} />
                </div>
              ))}
              <div style={styles.field}>
                <label style={styles.label}>Frecuencia de pago</label>
                <select value={form.frecuencia} onChange={e => setForm({ ...form, frecuencia: e.target.value })} style={styles.input}>
                  <option value="diario">Diario</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Observaciones</label>
                <textarea value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} style={{ ...styles.input, height: '80px', resize: 'vertical' }} />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnCancel} onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" style={styles.btnPrimary} disabled={loading}>{loading ? 'Creando...' : 'Crear Crédito'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cuotasModal && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: '600px' }}>
            <h3 style={styles.modalTitle}>Cuotas — {cuotasModal.cliente_nombre}</h3>
            <table style={styles.table}>
              <thead><tr style={styles.thead}><th>#</th><th>Vence</th><th>Monto</th><th>Pagado</th><th>Estado</th></tr></thead>
              <tbody>
                {cuotasModal.cuotas.map(cu => (
                  <tr key={cu.id} style={styles.row}>
                    <td>{cu.numero}</td>
                    <td>{cu.fecha_vence}</td>
                    <td>{formatMoneda(parseFloat(cu.monto))}</td>
                    <td>{formatMoneda(parseFloat(cu.monto_pagado))}</td>
                    <td><span style={{ color: cu.estado === 'pagada' ? '#34d399' : cu.estado === 'vencida' ? '#f87171' : '#fbbf24' }}>{cu.estado}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.modalActions}>
              <button style={styles.btnCancel} onClick={() => setCuotasModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {refiModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Refinanciar crédito</h3>
            <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem' }}>
              <div style={{ color: 'var(--text-2)', fontSize: '0.8rem', marginBottom: 4 }}>Cliente: <span style={{ color: 'var(--text-1)' }}>{refiModal.cliente_nombre}</span></div>
              <div style={{ color: 'var(--text-2)', fontSize: '0.8rem', marginBottom: 4 }}>Cuotas pendientes: <span style={{ color: 'var(--text-1)' }}>{refiModal.total_cuotas - refiModal.cuotas_pagadas}</span></div>
              <div style={{ color: 'var(--text-2)', fontSize: '0.8rem' }}>Saldo a refinanciar: <span style={{ color: '#fbbf24', fontWeight: 700 }}>{formatMoneda((refiModal.monto || 0) - (refiModal.total_pagado || 0))}</span></div>
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', margin: '0 0 1rem', lineHeight: 1.6 }}>
              Se cancelarán las cuotas pendientes del crédito actual y se creará un nuevo crédito con el saldo restante.
            </p>
            <form onSubmit={refinanciar}>
              <div style={styles.field}>
                <label style={styles.label}>Nueva tasa de interés (%)</label>
                <input type="number" step="0.01" min="0" value={refiForm.tasa_interes} onChange={e => setRefiForm({ ...refiForm, tasa_interes: e.target.value })} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Número de nuevas cuotas</label>
                <input type="number" min="1" value={refiForm.num_cuotas} onChange={e => setRefiForm({ ...refiForm, num_cuotas: e.target.value })} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Frecuencia de pago</label>
                <select value={refiForm.frecuencia} onChange={e => setRefiForm({ ...refiForm, frecuencia: e.target.value })} style={styles.input}>
                  <option value="diario">Diario</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Fecha de inicio del nuevo crédito</label>
                <input type="date" value={refiForm.fecha_inicio} onChange={e => setRefiForm({ ...refiForm, fecha_inicio: e.target.value })} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Observaciones</label>
                <input type="text" value={refiForm.observaciones} onChange={e => setRefiForm({ ...refiForm, observaciones: e.target.value })} style={styles.input} />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnCancel} onClick={() => setRefiModal(null)}>Cancelar</button>
                <button type="submit" style={{ ...styles.btnPrimary, background: '#d97706' }} disabled={loading}>{loading ? 'Refinanciando...' : 'Confirmar refinanciación'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { margin: 0, color: 'var(--text-1)', fontSize: '1.5rem' },
  btnPrimary: { background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: 700, cursor: 'pointer' },
  btnEdit: { background: 'var(--bg-hover)', color: '#38bdf8', border: 'none', borderRadius: '6px', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem' },
  btnCancel: { background: 'var(--bg-hover)', color: 'var(--text-1)', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer' },
  pill: { border: 'none', borderRadius: '20px', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, textTransform: 'capitalize' },
  tableWrap: { background: 'var(--bg-card)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--bg-app)', color: 'var(--text-2)', textAlign: 'left', fontSize: '0.8rem' },
  row: { borderBottom: '1px solid var(--border)', color: 'var(--text-1s)', fontSize: '0.9rem' },
  empty: { textAlign: 'center', color: 'var(--text-3)', padding: '2rem' },
  progressWrap: { background: 'var(--bg-hover)', borderRadius: '4px', height: '8px', width: '80px' },
  progressBar: { background: '#38bdf8', borderRadius: '4px', height: '8px', transition: 'width 0.3s' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: 'var(--bg-card)', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 1.5rem', color: 'var(--text-1)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', color: 'var(--text-2)', fontSize: '0.85rem', marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.65rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: '0.95rem', boxSizing: 'border-box' }
};
